/**
 * @file        src/components/GlobalSearch.jsx
 * @description Global admin search. Debounced query to /api/search; grouped
 *              dropdown results; ⌘K/Ctrl-K focus; arrow/enter/escape keys;
 *              click or Enter navigates to the result's page.
 * @author      Kinetika Labs
 * @created     2026-07-02
 * @changelog
 *   - 2026-07-02 Claude — Initial (global search).
 *   - 2026-10-09 Claude — Placeholder names hotel + insurance (now searched too).
 *   - 2026-08-04 Claude — Typeahead requests are `silent`: they have their own
 *                          inline indicator, so they skip the global <LoadBar />
 *                          (a bar strobing on every keystroke reads as noise).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Icon from './Icon';
import { get } from '../api/client';

export default function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  // ⌘K / Ctrl-K focuses the search from anywhere.
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close on outside click.
  useEffect(() => {
    function onDown(e) { if (!boxRef.current?.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Debounce the term.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const { data: groups, isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => get('/search', { params: { q: debounced }, silent: true }).then((r) => r.data),
    enabled: debounced.length >= 2,
    staleTime: 15_000,
  });

  // Flatten for keyboard navigation.
  const flat = useMemo(() => {
    const out = [];
    (groups || []).forEach((g) => g.items.forEach((it) => out.push({ ...it, type: g.type })));
    return out;
  }, [groups]);

  useEffect(() => { setActive(0); }, [debounced, groups]);

  function go(item) {
    if (!item) return;
    setOpen(false);
    setTerm('');
    navigate(item.path);
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); return; }
    if (!flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(flat[active]); }
  }

  const showDropdown = open && debounced.length >= 2;
  let flatIndex = -1;

  return (
    <div className="search" ref={boxRef}>
      <Icon name="search" />
      <input
        ref={inputRef}
        type="text"
        placeholder="Search flight, hotel, insurance bookings, customers…"
        value={term}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      <kbd>⌘K</kbd>

      {showDropdown ? (
        <div className="search-drop">
          {isFetching && !groups ? (
            <div className="search-empty">Searching…</div>
          ) : !flat.length ? (
            <div className="search-empty">No matches for “{debounced}”.</div>
          ) : (
            (groups || []).map((g) => (
              <div className="search-group" key={g.type}>
                <div className="search-group-head"><Icon name={g.icon} size={13} /> {g.type}</div>
                {g.items.map((it) => {
                  flatIndex += 1;
                  const idx = flatIndex;
                  return (
                    <button
                      type="button"
                      key={`${g.type}-${it.id}`}
                      className={`search-item${idx === active ? ' active' : ''}`}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => go(it)}
                    >
                      <span className="search-item-title">{it.title}</span>
                      {it.subtitle ? <span className="search-item-sub">{it.subtitle}</span> : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
